// useInView.ts
import { useEffect, useRef, useState } from 'react';

interface UseInViewOptions extends IntersectionObserverInit {
  triggerOnce?: boolean;
}

/**
 * Custom hook to detect when an element is in view.
 * @param options - IntersectionObserverInit options with optional triggerOnce flag.
 * @returns A tuple containing the ref object and a boolean indicating if the element is in view.
 */
const useInView = <T extends Element = HTMLDivElement>(
  options?: UseInViewOptions
): [React.RefObject<T>, boolean] => {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  const [hasBeenInView, setHasBeenInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(([entry]) => {
      const isIntersecting = entry.isIntersecting;

      if (options?.triggerOnce) {
        if (isIntersecting && !hasBeenInView) {
          setInView(true);
          setHasBeenInView(true);
        }
      } else {
        setInView(isIntersecting);
      }
    }, options);

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [options, hasBeenInView]);

  return [ref, inView];
};

export default useInView;
